import { devError } from './logger.js';

/**
 * 郵便番号検索ユーティリティ
 * zipcloud.ibsnet.co.jp の郵便番号検索APIを使用
 * 無料・登録不要
 */

/**
 * 郵便番号から住所を検索
 * @param {string} postalCode - 郵便番号（ハイフンあり/なし両方対応）
 * @returns {Promise<{success: boolean, address: string, prefecture: string, city: string, town: string, error?: string}>}
 */
export async function searchAddressByPostalCode(postalCode) {
  try {
    // 郵便番号の正規化（ハイフンを削除、数字のみに）
    const normalizedCode = postalCode.replace(/[^\d]/g, '');
    
    // 郵便番号のバリデーション（7桁）
    if (normalizedCode.length !== 7) {
      return {
        success: false,
        address: '',
        prefecture: '',
        city: '',
        town: '',
        error: '郵便番号は7桁で入力してください'
      };
    }
    
    // zipcloud APIに問い合わせ
    const response = await fetch(
      `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${normalizedCode}`
    );
    
    if (!response.ok) {
      throw new Error('郵便番号検索APIへのアクセスに失敗しました');
    }
    
    const data = await response.json();
    
    // エラーチェック
    if (data.status !== 200) {
      return {
        success: false,
        address: '',
        prefecture: '',
        city: '',
        town: '',
        error: data.message || '郵便番号が見つかりませんでした'
      };
    }
    
    // 結果が空の場合
    if (!data.results || data.results.length === 0) {
      return {
        success: false,
        address: '',
        prefecture: '',
        city: '',
        town: '',
        error: '該当する住所が見つかりませんでした'
      };
    }
    
    // 最初の結果を使用（複数ある場合は最初のもの）
    const result = data.results[0];
    
    // 住所を組み立て
    const prefecture = result.address1 || ''; // 都道府県
    const city = result.address2 || '';       // 市区町村
    const town = result.address3 || '';       // 町域
    const fullAddress = `${prefecture}${city}${town}`;
    
    return {
      success: true,
      address: fullAddress,
      prefecture: prefecture,
      city: city,
      town: town
    };
    
  } catch (error) {
    devError('郵便番号検索エラー:', error);
    return {
      success: false,
      address: '',
      prefecture: '',
      city: '',
      town: '',
      error: 'ネットワークエラーが発生しました'
    };
  }
}

/**
 * 郵便番号をフォーマット（xxx-xxxx形式）
 * @param {string} postalCode - 郵便番号
 * @returns {string} フォーマットされた郵便番号
 */
export function formatPostalCode(postalCode) {
  // 数字のみを抽出
  const numbers = postalCode.replace(/[^\d]/g, '');
  
  // 7桁未満の場合はそのまま返す
  if (numbers.length < 3) {
    return numbers;
  }
  
  // 3桁-4桁の形式にフォーマット
  if (numbers.length <= 7) {
    return numbers.slice(0, 3) + (numbers.length > 3 ? '-' + numbers.slice(3, 7) : '');
  }
  
  // 7桁を超える場合は7桁まで
  return numbers.slice(0, 3) + '-' + numbers.slice(3, 7);
}

/**
 * 郵便番号の入力値を検証
 * @param {string} postalCode - 郵便番号
 * @returns {{valid: boolean, error: string}} 検証結果
 */
export function validatePostalCode(postalCode) {
  const normalizedCode = postalCode.replace(/[^\d]/g, '');
  
  if (normalizedCode.length === 0) {
    return { valid: true, error: '' }; // 空欄は許可
  }
  
  if (normalizedCode.length !== 7) {
    return { valid: false, error: '郵便番号は7桁で入力してください' };
  }
  
  return { valid: true, error: '' };
}

